import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Linkedin, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoMark from "@/assets/new-site/nav-logo-mark.svg";
import wordmark from "@/assets/primary-wordmark-plum.png";
import { CookieConsentBanner } from "./CookieConsentBanner";
import { openCookieSettings } from "@/lib/cookieConsent";

export const navLinks = [
  { to: "/about-us", label: "About Us" },
  { to: "/solution", label: "Solution" },
  { to: "/sectors", label: "Sectors" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/news", label: "News" },
];

export function NsButton({
  children,
  variant = "outline",
  onClick,
  to,
  className = "",
}: {
  children: React.ReactNode;
  variant?: "outline" | "lime" | "light" | "white";
  onClick?: () => void;
  to?: string;
  className?: string;
}) {
  const styles = {
    outline: "border-ns-plum text-ns-plum hover:bg-ns-plum hover:text-ns-white",
    lime: "border-ns-plum bg-ns-lime text-ns-plum hover:brightness-95",
    light: "border-ns-white text-ns-white hover:bg-ns-white hover:text-ns-plum",
    white: "border-ns-white bg-ns-white text-ns-plum hover:brightness-95",
  }[variant];
  const cls = `inline-flex h-6 items-center justify-center whitespace-nowrap rounded-full border px-5 text-[12px] md:text-[14px] font-medium transition-colors ${styles} ${className}`;
  if (to) return <Link to={to} className={cls}>{children}</Link>;
  return <button type="button" onClick={onClick} className={cls}>{children}</button>;
}

export function NewSiteNav({ onContact }: { onContact: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-ns-white bg-ns-white">
      <div className="flex h-[63px] md:h-[72px] items-center justify-between px-6 md:px-[30px]">
        <Link to="/" aria-label="Snapshot Live home">
          <img src={logoMark} alt="Snapshot Live" width={44} height={23} className="h-[22.86px] w-[44px]" />
        </Link>
        <nav className="hidden md:flex gap-8 text-[14px] font-medium text-ns-plum">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} className="hover:opacity-70">{l.label}</Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <button onClick={onContact} className="text-[14px] font-medium text-ns-plum hover:opacity-70">Contact Us</button>
          <NsButton variant="lime" to="/login">Log In</NsButton>
        </div>
        <Button variant="ghost" size="icon" className="h-11 w-11 md:hidden text-ns-plum hover:bg-ns-plum/5" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open} aria-controls="ns-mobile-menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </div>
      {open && (
        <div id="ns-mobile-menu" role="dialog" aria-modal="true" aria-label="Menu" className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-ns-lime p-6 font-archivo text-ns-plum md:hidden">
          <Button variant="ghost" size="icon" autoFocus onClick={() => setOpen(false)} aria-label="Close menu" className="-mr-3 -mt-3 h-11 w-11 self-end text-ns-plum hover:bg-ns-plum/10 hover:text-ns-plum">
            <X size={24} strokeWidth={1.5} />
          </Button>
          <nav aria-label="Mobile navigation" className="flex flex-1 flex-col items-center justify-center gap-9 py-10 text-[14px] font-medium">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="hover:opacity-70">{l.label}</Link>
            ))}
            <button type="button" onClick={() => { setOpen(false); onContact(); }} className="hover:opacity-70">Contact Us</button>
            <Link to="/login" className="rounded-[36px] border border-ns-plum px-5 py-[5px] text-[12px] hover:bg-ns-plum/10">Log in</Link>
          </nav>
        </div>
      )}
    </header>
  );
}

const footerCols = [
  { title: "Company", links: [["About Us", "/about-us"], ["Our Solution", "/solution"], ["Case Studies", "/case-studies"], ["Sectors", "/sectors"], ["News", "/news"]] },
  { title: "Legal", links: [["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"], ["Cookie Policy", "/cookies"], ["Cookie Settings", "#cookie-settings"], ["Legal", "/disclaimer"], ["Data Protection", "/data-protection"], ["Environmental", "/environmental"]] },
];

/** Figma: desktop-footer */
export function NewSiteFooter({ onContact }: { onContact?: () => void }) {
  return (
    <>
    <CookieConsentBanner />
    <footer className="ns-footer flex flex-col gap-6 overflow-hidden bg-ns-white px-6 md:px-[72px] py-16 md:py-[120px] text-ns-plum">
      <div className="flex flex-col lg:flex-row items-start justify-between gap-12">
        <img src={wordmark} alt="Snapshot Live" className="h-auto w-[260px] max-w-full lg:w-[390px] lg:min-w-0 lg:shrink object-contain" />
        <div className="grid grid-cols-2 md:flex shrink-0 gap-10 md:gap-[67px] text-[12px] font-medium leading-[19.1px]">
          {footerCols.map((c) => (
            <div key={c.title} className="flex md:w-[150px] flex-col gap-4">
              <p>{c.title}</p>
              <ul className="flex flex-col gap-[10px] text-ns-plum/50">
                {c.links.map(([label, to]) => (
                  <li key={label}>{to === "#cookie-settings" ? (<button type="button" onClick={openCookieSettings} className="text-left hover:text-ns-plum">{label}</button>) : (<Link to={to} className="hover:text-ns-plum">{label}</Link>)}</li>
                ))}
                {onContact && (
                  <li><button type="button" onClick={onContact} className="text-left hover:text-ns-plum">Contact Us</button></li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div className="hidden md:block h-[37px]" />
      <div className="h-px w-full bg-ns-plum/20" />
      <div className="flex flex-col md:flex-row md:justify-between gap-2 text-[12px] font-medium leading-[19.1px] text-ns-plum/50">
        <p>Autonomous visual intelligence, anywhere.</p>
        <div className="flex items-center justify-center md:justify-start gap-4">
          <a href="https://www.linkedin.com/company/108945600/" target="_blank" rel="noopener noreferrer" aria-label="Snapshot Live on LinkedIn" className="hover:text-ns-plum"><Linkedin className="h-[17px] w-[17px]" /></a>
          <a href="https://www.instagram.com/snap_shotlive/" target="_blank" rel="noopener noreferrer" aria-label="Snapshot Live on Instagram" className="hover:text-ns-plum"><Instagram className="h-5 w-5" /></a>
        </div>
        <p>© 2026 Snapshot Live. All rights reserved.</p>
      </div>
    </footer>
    </>
  );
}
